import * as React from 'react';
import { ACTION_SELECT_MAP_PIN } from 'utils/analytics/eventTypeConstantsAmplitude';
import { CATEGORY_CITYWIDE_SEARCH } from 'utils/analytics/eventTypesConstants';
import { LoadGoogleMaps } from 'components/maps/LoadGoogleMaps';
import { SearchHandlerContext } from 'components/search/SearchContext';
import { SearchQueryStringParams } from 'components/search/utils';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useGoogleMapsContext } from 'providers/withApplicationContext';
import { useMapData } from 'hooks/search/useMapData';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import ConsumerMap from 'components/maps/ConsumerMap';
import ConsumerMarker from 'components/maps/ConsumerMarker';

interface AvailabilitySearchMapProps {
  disableMarkerSelection?: boolean;
  gestureHandling?: google.maps.MapOptions['gestureHandling'];
  hasControls?: boolean;
  latOffsetFactor?: number;
  zoom?: google.maps.MapOptions['zoom'];
  zoomControl?: google.maps.MapOptions['zoomControl'];
  onMarkerClick?(businessId: number): void;
}

const AvailabilitySearchMap: React.FunctionComponent<AvailabilitySearchMapProps> = ({
  disableMarkerSelection,
  gestureHandling,
  hasControls,
  latOffsetFactor,
  zoom,
  onMarkerClick,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const { loaded, error } = useGoogleMapsContext();
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  const { setSelectedBusinessId, selectedBusinessId, analyticsSearchContext } = {
    ...React.useContext(SearchHandlerContext),
  };
  const metroArea = useCurrentMetroArea();

  const data = useMapData();
  const { markers, focusedLocations } = data;
  const [selectedMarker, setSelectedMarker] = React.useState<number | undefined>(
    selectedBusinessId
  );

  React.useEffect(() => {
    setSelectedMarker(selectedBusinessId);
  }, [selectedBusinessId]);

  const handleMarkerClick = (businessId: number) => {
    const selectedMarker = markers.find((marker) => marker.id === businessId);
    // todo [BRM 2021/10/24] consider a value. Distance if location set?
    window.analytics.sendEvent(
      CATEGORY_CITYWIDE_SEARCH,
      'Map pin clicked',
      selectedMarker?.businessName
    );

    const data = {
      businessName: selectedMarker?.businessName,
      searchContext: analyticsSearchContext,
    };
    amplitudeSendEvent(ACTION_SELECT_MAP_PIN, data);

    ga4SendEvent('select_map_pin', data);
    if (onMarkerClick) {
      onMarkerClick(businessId);
    }
    if (disableMarkerSelection) {
      setSelectedBusinessId && setSelectedBusinessId(businessId);
      return;
    }
    setSelectedMarker(businessId);
    setSelectedBusinessId && setSelectedBusinessId(businessId);
  };

  return (
    <>
      <LoadGoogleMaps />
      <ConsumerMap
        gestureHandling={gestureHandling}
        hasControls={hasControls}
        isMapLoaded={loaded}
        loadError={error}
        latOffsetFactor={latOffsetFactor}
        locations={focusedLocations}
        locationsSortedGeographically={Boolean(queryStringParams.latlng)}
        markers={markers}
        metroArea={metroArea}
        onMapClick={() => {
          setSelectedMarker(undefined);
          setSelectedBusinessId && setSelectedBusinessId(undefined);
        }}
        selectedBusinessId={selectedBusinessId}
        showCurrentLocation={true}
        zoom={zoom}
      >
        {markers.map((marker) => (
          <ConsumerMarker
            key={marker.id}
            id={marker.id}
            isSelected={marker.id === selectedMarker}
            lat={marker.location.lat}
            lng={marker.location.lng}
            onClick={handleMarkerClick}
            label={marker.businessName}
            pinType={marker.pinType}
          />
        ))}
      </ConsumerMap>
    </>
  );
};

export default AvailabilitySearchMap;
